import React, { useContext, useEffect, useState } from 'react';
import { UserContext, UserData } from '../UserAndProfile/UserContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import {
  Box, Typography, Paper, Chip, Divider, CircularProgress
} from '@mui/material';
import { Quiz, LocalFireDepartment, TrendingUp } from '@mui/icons-material';
import { getCurrentWeek, getLinkedStudentRef, getParentStatusFromWeek, getSkillsImprovedThisWeek } from '../StudyPlan/studyPlanUtils';

const StudentProgressDisplay: React.FC = () => {
  const { user } = useContext(UserContext);
  const [student, setStudent] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);

  const linkedRef = user ? getLinkedStudentRef(user) : null;

  useEffect(() => {
    if (!linkedRef?.linkedUserFirebaseId) {
      setStudent(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const snap = await getDoc(doc(db, 'users', linkedRef.linkedUserFirebaseId!));
        if (!cancelled && snap.exists()) {
          setStudent({ uid: snap.id, ...snap.data() } as UserData);
        }
      } catch (err) {
        console.error('Failed to fetch linked student:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [linkedRef?.linkedUserFirebaseId]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={28} /></Box>;
  }

  if (!student) {
    return (
      <Typography variant="body2" color="text.secondary">
        Link an active student account to see their progress here.
      </Typography>
    );
  }

  const currentWeek = student.studyPlan ? getCurrentWeek(student.studyPlan, new Date()) : null;
  const status = getParentStatusFromWeek(currentWeek);
  const daysActive = currentWeek?.activeDates?.length || 0;
  const questionsThisWeek = currentWeek?.questionsAttempted || 0;
  const skillsImproved = student.studyPlan && currentWeek
    ? getSkillsImprovedThisWeek(student, currentWeek.weekNumber, 4)
    : [];

  const diagnosticAttempt = student.testAttempts?.find(t => t.testType === 'diagnostic' && t.completed);

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          {student.firstName}'s Progress
        </Typography>
        <Chip label={status.label} color={status.color} size="small" />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
        <Paper elevation={1} sx={{ p: 2, textAlign: 'center', flex: 1, minWidth: { xs: '100%', sm: 140 } }}>
          <Quiz color="primary" sx={{ mb: 0.5 }} />
          <Typography variant="h6">{questionsThisWeek}</Typography>
          <Typography variant="body2" color="text.secondary">Questions this week</Typography>
        </Paper>
        <Paper elevation={1} sx={{ p: 2, textAlign: 'center', flex: 1, minWidth: { xs: '100%', sm: 140 } }}>
          <LocalFireDepartment color="error" sx={{ mb: 0.5 }} />
          <Typography variant="h6">{daysActive}</Typography>
          <Typography variant="body2" color="text.secondary">Days active</Typography>
        </Paper>
        <Paper elevation={1} sx={{ p: 2, textAlign: 'center', flex: 1, minWidth: { xs: '100%', sm: 140 } }}>
          <TrendingUp color="success" sx={{ mb: 0.5 }} />
          <Typography variant="h6">{skillsImproved.length > 0 ? `+${skillsImproved[0].change}%` : '—'}</Typography>
          <Typography variant="body2" color="text.secondary">
            {skillsImproved.length > 0 ? `Top skill: ${skillsImproved[0].skill}` : 'No improvement yet'}
          </Typography>
        </Paper>
      </Box>

      <Divider sx={{ my: 2 }} />

      {diagnosticAttempt ? (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Diagnostic score</Typography>
          <Chip
            label={`Total: ${diagnosticAttempt.diagnosticTotalScores?.lower ?? '?'}-${diagnosticAttempt.diagnosticTotalScores?.upper ?? '?'}`}
            size="small" color="secondary"
          />
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Diagnostic not completed yet.
        </Typography>
      )}

      {skillsImproved.length > 0 && (
        <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Conversation starter</Typography>
          <Typography variant="body2" color="text.secondary">
            Ask {student.firstName} about {skillsImproved[0].skill} — they improved {skillsImproved[0].change}% this week on {skillsImproved[0].difficulty} questions.
          </Typography>
        </Paper>
      )}

      <Typography variant="caption" color="text.secondary" display="block">
        Updates when your student practices. Weekly summary also sent by email.
      </Typography>
    </Box>
  );
};

export default StudentProgressDisplay;
